import { Upload, message, Progress, Button, FormInstance } from "antd";
import { IconTrash, IconUpload } from "@tabler/icons-react";
import axios from "axios";
import { useState, useEffect, useRef } from "react";
import { ENDPOINTS } from "../constants/endpoints.constant";

type ImageItem = {
  url?: string;
  uploading: boolean;
  percent: number;
  uid?: string;
};

const CustomImageUploader = ({
  max,
  form,
  name,
  initialValue = [],
  onUploadStatusChange,
}: {
  max: number;
  form: FormInstance;
  name: string;
  height?: string;
  initialValue?: ImageItem[];
  onUploadStatusChange?: (uploading: boolean) => void;
}) => {
  const [images, setImages] = useState<ImageItem[]>(initialValue);
  const imagesRef = useRef<ImageItem[]>(images);
  const pendingRef = useRef<{ files: File[]; scheduled: boolean }>({
    files: [],
    scheduled: false,
  });

  // Keep ref in sync for async callbacks
  useEffect(() => {
    imagesRef.current = images;
    const isAnyUploading = images.some((img) => img.uploading);
    onUploadStatusChange?.(isAnyUploading);
  }, [images, onUploadStatusChange]);

  const batchUpload = async (files: File[]) => {
    const startIndex = imagesRef.current.length;
    const newImages: ImageItem[] = files.map(() => ({
      uploading: true,
      percent: 0,
    }));

    setImages((prev) => [...prev, ...newImages]);

    const formData = new FormData();
    files.forEach((file, i) => {
      formData.append(`attachment[${i}]`, file);
    });
    formData.append("is_visible", "false");

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_APP_API_URL}${ENDPOINTS.attachment}`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: ({ loaded, total }) => {
            if (total) {
              const percent = Math.round((loaded / total) * 100);
              setImages((prev) =>
                prev.map((img, i) =>
                  i >= startIndex && i < startIndex + files.length
                    ? { ...img, percent }
                    : img,
                ),
              );
            }
          },
        },
      );

      if (response?.data?.status) {
        const uploadedData = response.data?.data as Array<{
          url: string;
          uid: string;
        }>;

        setImages((prev) => {
          const updated = prev.map((img, i) => {
            const uploadIdx = i - startIndex;
            if (
              i >= startIndex &&
              i < startIndex + files.length &&
              uploadedData[uploadIdx]
            ) {
              return {
                ...img,
                uploading: false,
                url: uploadedData[uploadIdx].url,
                uid: uploadedData[uploadIdx].uid,
              };
            }
            return img;
          });

          // Update form field
          form.setFieldValue(
            name,
            updated.filter((img) => img.uid).map((img) => img.uid),
          );

          return updated;
        });
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      message.error(
        "The attachment may not be greater than 5MB or upload failed.",
      );
      setImages((prev) =>
        prev.filter((_, i) => i < startIndex || i >= startIndex + files.length),
      );
    }
  };

  const handleBeforeUpload = (_file: File, fileList: File[]) => {
    const validFiles = fileList.filter((file) => {
      const isValidType = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/svg+xml",
      ].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5 MB
      if (!isValidType) {
        message.error(`${file.name} must be JPEG, PNG, JPG, or SVG`);
      }
      if (!isValidSize) {
        message.error(`${file.name} exceeds 5 MB size limit`);
      }
      return isValidType && isValidSize;
    });

    if (validFiles.length === 0) return false;

    if (!pendingRef.current.scheduled) {
      const remaining = max - imagesRef.current.length;
      const filesToUpload = validFiles.slice(0, remaining);
      pendingRef.current = { files: filesToUpload, scheduled: true };

      setTimeout(() => {
        const files = pendingRef.current.files;
        pendingRef.current = { files: [], scheduled: false };
        if (files.length > 0) {
          batchUpload(files);
        }
      }, 0);
    }
    return false;
  };

  const handleRemove = (idx: number) => {
    setImages((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      form.setFieldValue(
        name,
        updated.filter((img) => img.uid).map((img) => img.uid),
      );
      return updated;
    });
  };

  return (
    <div className="mb-10">
      <div
        className={`grid grid-cols-1 gap-20 ${
          max > 1 && images.length > 0 ? "sm:!grid-cols-2" : "sm:grid-cols-1"
        }`}
      >
        {images.map((img, index) => (
          <div
            key={index}
            className="relative w-full group overflow-hidden rounded-lg border border-gray-100"
            style={{ aspectRatio: "2/1" }}
          >
            {img.uploading ? (
              <div className="flex flex-col items-center justify-center h-full p-4 bg-gray-50">
                <Progress
                  type="circle"
                  percent={img.percent}
                  size={40}
                  strokeWidth={10}
                />
                <span className="mt-2 text-xs text-gray-500 font-medium">
                  Uploading...
                </span>
              </div>
            ) : (
              <>
                {img.url?.toLowerCase().endsWith(".pdf") ? (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center font-semibold text-gray-500">
                    PDF Document
                  </div>
                ) : (
                  <img
                    src={img.url}
                    alt={`Uploaded ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                )}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 backdrop-blur-sm">
                  <Button
                    danger
                    type="primary"
                    shape="circle"
                    icon={<IconTrash size={20} />}
                    onClick={() => handleRemove(index)}
                    className="shadow-lg border-none hover:scale-110 transition-transform"
                  />
                </div>
              </>
            )}
          </div>
        ))}

        {images.length < max && (
          <Upload.Dragger
            beforeUpload={handleBeforeUpload}
            showUploadList={false}
            accept="image/*,application/pdf"
            multiple={max > 1}
            className="!w-full customUploader hover:border-primary transition-colors"
          >
            <div className="p-8">
              <div className="flex justify-center mb-4 text-primary">
                <IconUpload size={32} />
              </div>
              <strong className="text-primary block text-lg mb-1">
                Upload File
              </strong>
              <div className="text-gray-400">or drag and drop images</div>
              <div className="mt-2 text-xs text-gray-400">
                Maximum size: 5MB
              </div>
            </div>
          </Upload.Dragger>
        )}
      </div>
    </div>
  );
};

export default CustomImageUploader;
